/**
 * Document Scanning Service
 *
 * Sends scan commands via the Bridge system. The bridge desktop app
 * receives the command and triggers the connected document scanner
 * (e.g. Fujitsu fi-7160). Local scans arrive as bridge events; backend
 * fallback scans are recovered from the command result or persisted event.
 */

import {
  bridgeService,
  type BridgeCommand,
  type DeviceEvent,
} from "./bridgeService";
import { deviceBridgeService } from "./deviceBridge.service";
import type { BridgeResponse } from "./deviceBridge.service";
import { workstationPreferenceService } from "./workstationPreference.service";

const SCANNER_DEVICE_KEY = "document-scanner-device-id";
const SCANNER_DEVICE_NAME_KEY = "document-scanner-device-name";
const BACKEND_SCAN_EVENT_LIMIT = 1;
const BACKEND_SCAN_EVENT_TOLERANCE_MS = 5000;
const BACKEND_SCAN_RECOVERY_ATTEMPTS = 3;
const BACKEND_SCAN_RECOVERY_DELAY_MS = 250;
const BACKEND_SCAN_FAILED_MESSAGE =
  "The scanner could not produce a document. Check the scanner and try again.";
const BACKEND_SCAN_TIMEOUT_MESSAGE =
  "The scanner did not finish in time. Check the scanner and try again.";
const BACKEND_SCAN_MISSING_DOCUMENT_MESSAGE =
  "The scan completed, but the document could not be retrieved. Please try again.";
const BACKEND_SCAN_UNAVAILABLE_MESSAGE =
  "The scanner could not be reached. Please try again.";
const LOCAL_SCAN_TIMEOUT_MS = 120000;

export type DocumentScannerStatus =
  | "ready" // bridge device configured
  | "no-device" // no bridge device selected
  | "scanning"; // scan in progress

export interface ScanResult {
  success: boolean;
  file?: File;
  fileName?: string;
  mimeType?: string;
  base64Data?: string;
  error?: string;
}

export interface ScanOptions {
  waitForEventOnly?: boolean;
}

export type BackendScanOutcome =
  | { status: "success"; file: File }
  | { status: "failed"; message: string };

interface ScanCommandResult {
  status: string;
  result?: Record<string, any>;
  completedAt: string | null;
}

function getScanFileDetails(data: Record<string, any>):
  | {
      base64Data: string;
      format: string;
    }
  | undefined {
  const base64Data = data.imageData ?? data.base64 ?? data.base64Data;
  const format =
    typeof data.format === "string" ? data.format.toLowerCase() : "pdf";
  if (typeof base64Data !== "string" || base64Data.length === 0)
    return undefined;
  if (!/^(pdf|png|jpe?g|tiff?)$/.test(format)) return undefined;
  return { base64Data, format };
}

function createScanFile(
  data: Record<string, any>,
  occurredAt: string
): File | undefined {
  const details = getScanFileDetails(data);
  if (!details) return undefined;
  const byteString = atob(details.base64Data);
  const bytes = Uint8Array.from(byteString, (character) =>
    character.charCodeAt(0)
  );
  const timestamp = new Date(occurredAt)
    .toISOString()
    .replace(/[:.]/g, "-")
    .slice(0, 23);
  const mimeType =
    details.format === "pdf" ? "application/pdf" : `image/${details.format}`;
  return new File([bytes], `scan_${timestamp}.${details.format}`, {
    type: mimeType,
  });
}

function getLocalScanFile(
  message: BridgeResponse,
  expectedDeviceId: string
): File | undefined {
  const raw = message as unknown as Record<string, unknown>;
  const outerType = (raw.type ?? raw.messageType ?? "") as string;
  const payload = message.payload ?? {};
  const eventType = (payload.eventType ?? outerType) as string;
  if (eventType.toLowerCase() !== "document_scanned") return undefined;
  if (payload.deviceId !== expectedDeviceId) return undefined;
  const data = (payload.data ?? payload) as Record<string, any>;
  const occurredAt =
    typeof payload.occurredAt === "string"
      ? payload.occurredAt
      : new Date().toISOString();
  return createScanFile(data, occurredAt);
}

function waitForLocalScanEvent(
  deviceId: string,
  trigger?: () => Promise<unknown>
): Promise<File> {
  return new Promise((resolve, reject) => {
    let settled = false;
    let unsubscribe: () => void = () => undefined;
    let timeout: ReturnType<typeof setTimeout>;
    const finish = (action: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      unsubscribe();
      action();
    };
    timeout = setTimeout(() => {
      finish(() => reject(new Error(BACKEND_SCAN_TIMEOUT_MESSAGE)));
    }, LOCAL_SCAN_TIMEOUT_MS);
    const handleMessage = (message: BridgeResponse) => {
      const file = getLocalScanFile(message, deviceId);
      if (file) finish(() => resolve(file));
    };
    unsubscribe = deviceBridgeService.onMessage(handleMessage);
    if (trigger) {
      trigger().catch((error) => finish(() => reject(error)));
    }
  });
}

function eventMatchesCommand(
  event: DeviceEvent,
  command: BridgeCommand
): boolean {
  if (event.eventType !== "document_scanned") return false;
  if (
    event.deviceId !== command.deviceId ||
    event.workstationId !== command.workstationId
  )
    return false;
  const occurredAt = new Date(event.occurredAt).getTime();
  const createdAt = new Date(command.createdAt).getTime();
  const completedAt = command.completedAt
    ? new Date(command.completedAt).getTime()
    : Date.now();
  return (
    occurredAt >= createdAt - BACKEND_SCAN_EVENT_TOLERANCE_MS &&
    occurredAt <= completedAt + BACKEND_SCAN_EVENT_TOLERANCE_MS
  );
}

function getScanRecoveryStart(command: BridgeCommand): string {
  const createdAt = new Date(command.createdAt).getTime();
  return new Date(createdAt - BACKEND_SCAN_EVENT_TOLERANCE_MS).toISOString();
}

function findMatchingScanEvent(
  events: DeviceEvent[],
  command: BridgeCommand
): DeviceEvent | undefined {
  const completedAt = command.completedAt
    ? new Date(command.completedAt).getTime()
    : Date.now();
  return events
    .filter((event) => eventMatchesCommand(event, command))
    .sort(
      (left, right) =>
        Math.abs(new Date(left.occurredAt).getTime() - completedAt) -
        Math.abs(new Date(right.occurredAt).getTime() - completedAt)
    )[0];
}

async function requestBackendScanFile(
  command: BridgeCommand
): Promise<File | undefined> {
  const { data } = await bridgeService.getEvents(command.workstationId, {
    deviceId: command.deviceId,
    eventType: "document_scanned",
    since: getScanRecoveryStart(command),
    page: 1,
    limit: BACKEND_SCAN_EVENT_LIMIT,
  });
  const event = findMatchingScanEvent(data.data, command);
  return event ? createScanFile(event.data, event.occurredAt) : undefined;
}

async function recoverBackendScanFile(
  command: BridgeCommand,
  attemptsRemaining = BACKEND_SCAN_RECOVERY_ATTEMPTS
): Promise<File | undefined> {
  try {
    const file = await requestBackendScanFile(command);
    if (file || attemptsRemaining <= 1) return file;
  } catch (error) {
    if (attemptsRemaining <= 1) throw error;
  }
  await new Promise((resolve) =>
    setTimeout(resolve, BACKEND_SCAN_RECOVERY_DELAY_MS)
  );
  return recoverBackendScanFile(command, attemptsRemaining - 1);
}

class DocumentScanningService {
  private deviceId: string | null = null;

  private deviceName: string | null = null;

  private currentStatus: DocumentScannerStatus = "no-device";

  private statusListeners: Set<(status: DocumentScannerStatus) => void> =
    new Set();

  private backendScanOutcomeListeners: Set<
    (outcome: BackendScanOutcome) => void
  > = new Set();

  constructor() {
    this.restoreDevice();
  }

  private restoreDevice(): void {
    try {
      // Try workstation preference first, then legacy keys
      const prefId =
        workstationPreferenceService.getSelectedDeviceId("document_scanner");
      const prefName =
        workstationPreferenceService.getSelectedDeviceName("document_scanner");
      if (prefId) {
        this.deviceId = prefId;
        this.deviceName = prefName;
        this.currentStatus = "ready";
        return;
      }

      const storedId = localStorage.getItem(SCANNER_DEVICE_KEY);
      const storedName = localStorage.getItem(SCANNER_DEVICE_NAME_KEY);
      if (storedId) {
        this.deviceId = storedId;
        this.deviceName = storedName;
        this.currentStatus = "ready";
      }
    } catch {
      // localStorage unavailable; ignore
    }
  }

  get status(): DocumentScannerStatus {
    return this.currentStatus;
  }

  get selectedDeviceId(): string | null {
    return this.deviceId;
  }

  get selectedDeviceName(): string | null {
    return this.deviceName;
  }

  get isReady(): boolean {
    return this.currentStatus === "ready";
  }

  onStatusChange(
    listener: (status: DocumentScannerStatus) => void
  ): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  onBackendScanOutcome(
    listener: (outcome: BackendScanOutcome) => void
  ): () => void {
    this.backendScanOutcomeListeners.add(listener);
    return () => this.backendScanOutcomeListeners.delete(listener);
  }

  private notifyStatusChange(): void {
    this.statusListeners.forEach((listener) => listener(this.currentStatus));
  }

  private notifyBackendScanOutcome(outcome: BackendScanOutcome): void {
    this.backendScanOutcomeListeners.forEach((listener) => listener(outcome));
  }

  /**
   * Configure the bridge device to use for document scanning.
   */
  setDevice(deviceId: string, deviceName: string): void {
    this.deviceId = deviceId;
    this.deviceName = deviceName;
    this.currentStatus = "ready";
    try {
      localStorage.setItem(SCANNER_DEVICE_KEY, deviceId);
      localStorage.setItem(SCANNER_DEVICE_NAME_KEY, deviceName);
    } catch {
      // ignore
    }
    this.notifyStatusChange();
  }

  /**
   * Remove the configured scanner device.
   */
  clearDevice(): void {
    this.deviceId = null;
    this.deviceName = null;
    this.currentStatus = "no-device";
    try {
      localStorage.removeItem(SCANNER_DEVICE_KEY);
      localStorage.removeItem(SCANNER_DEVICE_NAME_KEY);
    } catch {
      // ignore
    }
    this.notifyStatusChange();
  }

  getStatusMessage(): string {
    switch (this.currentStatus) {
      case "ready":
        return `Scanner: ${this.deviceName || "Unknown device"}`;
      case "scanning":
        return "Scanning document…";
      case "no-device":
        return "No scanner device configured";
      default:
        return "Unknown status";
    }
  }

  /**
   * Send a scan_document command to the bridge and wait for the result.
   * The bridge app triggers the scanner and returns the scanned file data.
   */
  async scanDocument(options: ScanOptions = {}): Promise<ScanResult> {
    if (!this.deviceId) {
      return { success: false, error: "No scanner device configured" };
    }

    const prevStatus = this.currentStatus;
    this.currentStatus = "scanning";
    this.notifyStatusChange();

    // ── Path 1: Local bridge (preferred — scan data arrives via document_scanned event) ──
    if (deviceBridgeService.isLocal()) {
      try {
        const trigger = options.waitForEventOnly
          ? undefined
          : () =>
              deviceBridgeService.sendCommand(
                {
                  type: "scan_document",
                  payload: {
                    deviceId: this.deviceId!,
                    format: "pdf",
                    colorMode: "color",
                    resolution: 300,
                  },
                },
                LOCAL_SCAN_TIMEOUT_MS
              );
        const file = await waitForLocalScanEvent(this.deviceId, trigger);
        return this.completeScanSuccess(file);
      } catch {
        if (options.waitForEventOnly) {
          return this.completeScanFailure(
            prevStatus,
            BACKEND_SCAN_TIMEOUT_MESSAGE
          );
        }
        // Local bridge failed — fall through to backend path.
      }
    }

    if (options.waitForEventOnly) {
      return this.completeScanFailure(
        prevStatus,
        BACKEND_SCAN_UNAVAILABLE_MESSAGE
      );
    }

    // ── Path 2: Backend relay (fallback) ──
    return this.scanDocumentViaBackend(prevStatus);
  }

  private async scanDocumentViaBackend(
    previousStatus: DocumentScannerStatus
  ): Promise<ScanResult> {
    try {
      const command = await this.createBackendScanCommand();
      const result = await this.waitForCommandResult(command.id);
      const completedCommand = { ...command, completedAt: result.completedAt };
      return await this.resolveBackendScan(completedCommand, result);
    } catch {
      return this.completeScanFailure(
        previousStatus,
        BACKEND_SCAN_UNAVAILABLE_MESSAGE
      );
    }
  }

  private async createBackendScanCommand(): Promise<BridgeCommand> {
    const { data } = await bridgeService.createCommand({
      deviceId: this.getConfiguredDeviceId(),
      commandType: "scan_document",
      payload: { format: "pdf", colorMode: "color", resolution: 300 },
      expiresInSeconds: 120,
    });
    return data;
  }

  private getConfiguredDeviceId(): string {
    if (!this.deviceId) throw new Error("Scanner device is not configured");
    return this.deviceId;
  }

  private async resolveBackendScan(
    command: BridgeCommand,
    result: ScanCommandResult
  ): Promise<ScanResult> {
    if (result.status !== "completed") {
      const message =
        result.status === "expired"
          ? BACKEND_SCAN_TIMEOUT_MESSAGE
          : BACKEND_SCAN_FAILED_MESSAGE;
      return this.completeScanFailure("ready", message);
    }
    const resultTimestamp = result.completedAt ?? command.createdAt;
    const resultFile = result.result
      ? createScanFile(result.result, resultTimestamp)
      : undefined;
    const file = resultFile ?? (await recoverBackendScanFile(command));
    if (!file)
      return this.completeScanFailure(
        "ready",
        BACKEND_SCAN_MISSING_DOCUMENT_MESSAGE
      );
    return this.completeScanSuccess(file);
  }

  private completeScanSuccess(file: File): ScanResult {
    this.currentStatus = "ready";
    this.notifyStatusChange();
    this.notifyBackendScanOutcome({ status: "success", file });
    return { success: true, file };
  }

  private completeScanFailure(
    status: DocumentScannerStatus,
    message: string
  ): ScanResult {
    this.currentStatus = status;
    this.notifyStatusChange();
    this.notifyBackendScanOutcome({ status: "failed", message });
    return { success: false, error: message };
  }

  /**
   * Poll the bridge command status until it completes, fails, or expires.
   */
  // eslint-disable-next-line class-methods-use-this
  private async waitForCommandResult(
    commandId: string,
    timeoutMs = 120000,
    pollIntervalMs = 2000
  ): Promise<ScanCommandResult> {
    const start = Date.now();
    // Polling is intentional here — we need sequential checks until the command completes
    /* eslint-disable no-await-in-loop */
    while (Date.now() - start < timeoutMs) {
      const { data: cmd } = await bridgeService.getCommand(commandId);
      if (
        cmd.status === "completed" ||
        cmd.status === "failed" ||
        cmd.status === "expired"
      ) {
        return {
          status: cmd.status,
          result: cmd.result ?? undefined,
          completedAt: cmd.completedAt,
        };
      }
      await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
    }
    /* eslint-enable no-await-in-loop */
    return {
      status: "expired",
      completedAt: null,
    };
  }

  /**
   * Open the browser file picker as a fallback so the user can select
   * a pre-scanned document from disk when no bridge device is available.
   */
  static async selectScannedFile(): Promise<File | null> {
    return new Promise((resolve) => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*,application/pdf";
      input.multiple = false;

      input.onchange = (e: Event) => {
        const files = (e.target as HTMLInputElement).files;
        resolve(files && files.length > 0 ? files[0] : null);
      };

      input.oncancel = () => resolve(null);
      input.click();
    });
  }

  static blobToFile(blob: Blob, fileName: string, mimeType: string): File {
    return new File([blob], fileName, { type: mimeType });
  }
}

export const documentScanningService = new DocumentScanningService();
